// inv/src/app/app-components/Deals.jsx
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Pagination from './../../components/ui/pagination';
import { fetchDeals } from './deals-modules/apiService';
import DealsTimelineView from './deals-modules/dealsTable';
import { Menu } from '../../components/ui/menu';

const Deals = ({
  setTotalDealsCount,
  dealsFilters,
  dealsCurrentPage,
  setDealsCurrentPage,
  setDealsTotalResults,
}) => {
  const [deals, setDeals] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const [sortOrder, setSortOrder] = useState('desc');
  const pageSize = 10;

  // --- sync with URL ----
  const location = useLocation();
  const navigate = useNavigate();
  const queryKey = 'page'; // <-- want ?p= — change this to 'p'

  // Read the page from the URL (and on back/forward navigation)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // fallback: also handle unexpected ?p=
    const p = Number(params.get(queryKey) || params.get('p') || 1);
    if (!Number.isNaN(p) && p !== dealsCurrentPage) {
      setDealsCurrentPage(p);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, queryKey]);

  const loadDeals = async () => {
    setIsLoading(true);
    try {
      const { data, totalCount } = await fetchDeals(
        dealsCurrentPage,
        pageSize,
        dealsFilters,
        sortOrder
      );
      setDeals(data);
      setTotalCount(totalCount);
      setTotalPages(Math.ceil(totalCount / pageSize));
      setTotalDealsCount(totalCount);
      setDealsTotalResults(totalCount);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDeals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealsCurrentPage, dealsFilters, sortOrder]);

  const handlePageChange = (page) => setDealsCurrentPage(page);

  // When sorting changes, set page to 1 and update URL (?page=1)
  const handleSortChange = (newSort) => {
    setDealsCurrentPage(1);
    setSortOrder(newSort);

    const params = new URLSearchParams(location.search);
    params.set(queryKey, '1');
    navigate({ search: `?${params.toString()}` }, { replace: true });
  };

  const getSortLabel = () => (sortOrder === 'desc' ? 'Newest first' : 'Oldest first');

  const sortMenuTabs = [
    {
      label: getSortLabel(),
      items: [
        { label: 'Newest first', onClick: () => handleSortChange('desc') },
        { label: 'Oldest first', onClick: () => handleSortChange('asc') },
      ],
    },
  ];

  return (
    <div aria-busy={isLoading ? 'true' : 'false'}>
      {/* Top panel */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <div className="flex-1 min-w-0">
          <Pagination
            currentPage={dealsCurrentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            useUrl
            queryKey={queryKey}
            className="w-full"
            disabled={isLoading}
          />
        </div>

        <div className="flex items-center gap-2 ml-4 shrink-0 whitespace-nowrap">
          <span className="text-sm text-stone-700 mr-2 hidden sm:inline">
            Sort by date
          </span>
          <Menu tabs={sortMenuTabs} disabled={isLoading} />
        </div>
      </div>

      {/* Skeleton/deals list */}
      <DealsTimelineView
        deals={deals}
        isLoading={isLoading}
        pageSize={pageSize}
      />

      {/* Bottom pagination */}
      <div className="mt-8">
        <Pagination
          currentPage={dealsCurrentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          useUrl
          queryKey={queryKey}
          disabled={isLoading}
        />
      </div>
    </div>
  );
};

export default Deals;
